import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useSaveLabRow } from '../../hooks/lab-hooks';
import {
  LabScenario,
  LabSuccessCriterion,
  labSlugify,
  LAB_EVALUATORS,
  LAB_REPORTED_EVALUATORS,
  LAB_TABLES,
} from '../../support/labService';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  labErrorMessage,
} from './LabShared';
import { LabSpecBuilder, LabSpecContext } from './LabSpecFields';
import LabStepEditorModal from './LabStepEditorModal';

export type LabCriterionEditorModalProps = {
  open: boolean;
  scenario: LabScenario;
  criterion: LabSuccessCriterion | null;
  onClose: () => void;
  onSaved?: (criterion: LabSuccessCriterion) => void;
};

const newCriterion = (scenario: LabScenario): Partial<LabSuccessCriterion> => ({
  scenario: scenario.id,
  key: '',
  name: '',
  description: '',
  order: ((scenario.success_criteria || []).reduce((max, c) => Math.max(max, c.order || 0), 0) || 0) + 10,
  weight: 1,
  required: true,
  evaluator: 'steps_passed',
  steps: [],
  hold_seconds: 0,
});

/**
 * Author a success criterion.
 *
 * A criterion is how a run is scored, which is a different question from whether its steps
 * ran. The evaluator decides where the verdict comes from, and the form only asks for the
 * inputs that evaluator actually reads - a fact assertion on a `steps_passed` criterion is
 * dead weight that reads like configuration.
 */
const LabCriterionEditorModal: React.FC<LabCriterionEditorModalProps> = ({
  open,
  scenario,
  criterion,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabSuccessCriterion>>(() => newCriterion(scenario));
  const [jsonErrors, setJsonErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formSeed, setFormSeed] = useState(0);
  /** The nested step editor, for "+ New step" without leaving this form. */
  const [newStep, setNewStep] = useState(false);

  const save = useSaveLabRow<Partial<LabSuccessCriterion> & { id?: number }>(LAB_TABLES.criterion);

  const steps = scenario.steps || [];

  useEffect(() => {
    if (!open) return;

    setDraft(
      criterion ? { ...criterion, steps: [...(criterion.steps || [])] } : newCriterion(scenario),
    );
    setJsonErrors({});
    setFormError(null);
    setNewStep(false);
    setFormSeed((seed) => seed + 1);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, criterion?.id]);

  const set = (patch: Partial<LabSuccessCriterion>) =>
    setDraft((current) => ({ ...current, ...patch }));

  /**
   * The key follows the name until the criterion exists, then stops.
   *
   * A saved criterion's key is what the proctor scoreboard, the triggers and any external report
   * refer to, so renaming "Tunnel is up" to "Tunnel established" has to leave the key alone -
   * otherwise a rename silently orphans every trigger watching it.
   */
  const setName = (name: string) => set(criterion ? { name } : { name, key: labSlugify(name) });

  const specContext = useMemo<LabSpecContext>(
    () => ({
      deviceKeys: (scenario.devices || []).map((device) => device.device_key).filter(Boolean),
      stepKeys: (scenario.steps || []).map((step) => step.key).filter(Boolean),
    }),
    [scenario.devices, scenario.steps],
  );

  const setJson = (field: keyof LabSuccessCriterion) => (
    value: Record<string, any> | null,
    error: string | null,
  ) => {
    setJsonErrors((current) => ({ ...current, [field]: error }));
    if (!error) set({ [field]: value } as Partial<LabSuccessCriterion>);
  };

  const toggleStep = (stepId: number) =>
    setDraft((current) => {
      const selected = current.steps || [];

      return {
        ...current,
        steps: selected.includes(stepId)
          ? selected.filter((id) => id !== stepId)
          : [...selected, stepId],
      };
    });

  const reported = LAB_REPORTED_EVALUATORS.includes(draft.evaluator as any);

  const handleSubmit = async () => {
    const firstJsonError = Object.entries(jsonErrors).find(([, error]) => !!error);

    if (firstJsonError) {
      setFormError(`${firstJsonError[0]}: ${firstJsonError[1]}`);
      return;
    }

    if (!draft.name?.trim()) {
      setFormError('A criterion needs a name.');
      return;
    }

    if (!draft.key?.trim()) {
      setFormError(
        'That name derives no key, and the key is what a proctor reports against. Give it letters or digits, not punctuation alone.',
      );
      return;
    }

    if (draft.evaluator === 'steps_passed' && !(draft.steps || []).length) {
      setFormError('A steps-passed criterion with no steps can never be met.');
      return;
    }

    if (draft.evaluator === 'fact_match' && !draft.fact_assertion) {
      setFormError('A fact-match criterion needs an assertion to match against.');
      return;
    }

    if (draft.evaluator === 'device_health' && !draft.health_assertion) {
      setFormError('A device-health criterion needs a health assertion.');
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync(draft);
      onSaved?.(saved as LabSuccessCriterion);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title={criterion ? `Edit criterion ${criterion.key}` : `New criterion in ${scenario.name}`}
      size="large"
      scrollable
      submitLabel={criterion ? 'Save criterion' : 'Create criterion'}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label="Name"
          value={draft.name || ''}
          onChange={(evt) => setName(evt.target.value)}
          helperText="What the scoreboard calls it. Written for whoever reads the result."
          fullWidth
        />

        <LabDerivedKey
          value={draft.key || ''}
          frozen={!!criterion}
          label="Criterion key"
          usage="The scoreboard, the triggers and any external report refer to the criterion by it."
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => set({ description: evt.target.value })}
          multiline
          minRows={2}
          fullWidth
        />

        <LabFieldRow columns={3}>
          <TextField
            label="Order"
            type="number"
            value={draft.order ?? 0}
            onChange={(evt) => set({ order: Number(evt.target.value) })}
            fullWidth
          />
          <TextField
            label="Weight"
            type="number"
            value={draft.weight ?? 1}
            onChange={(evt) => set({ weight: Number(evt.target.value) })}
            helperText="Share of the score."
            fullWidth
          />
          <TextField
            label="Hold (seconds)"
            type="number"
            value={draft.hold_seconds ?? 0}
            onChange={(evt) => set({ hold_seconds: Number(evt.target.value) })}
            helperText="How long it has to stay true before it counts. Use it where state flaps."
            fullWidth
          />
        </LabFieldRow>

        <LabCheckbox
          label="Required"
          checked={!!draft.required}
          onChange={(required) => set({ required })}
          helpText="A required criterion that is not met fails the run whatever the score says."
        />

        <LabOptionSelect
          label="Evaluator"
          value={draft.evaluator}
          options={LAB_EVALUATORS}
          onChange={(evaluator) => set({ evaluator })}
        />

        {reported && (
          <p className="m-0 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
            Nothing recomputes this one. The verdict comes from a person or another system, and
            re-evaluating the run leaves it alone - which is the point, because deriving it would
            throw away the only real answer.
          </p>
        )}

        {draft.evaluator === 'steps_passed' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-gray-700">Steps that have to pass</div>
              <StbButton role="add" size="xsmall" onClick={async () => setNewStep(true)}>
                + New step
              </StbButton>
            </div>
            <div className="mt-1 max-h-48 overflow-y-auto rounded border border-gray-200 p-2">
              {steps.length ? (
                steps.map((step) => (
                  <label className="flex items-center gap-2 text-sm" key={step.id}>
                    <input
                      type="checkbox"
                      checked={(draft.steps || []).includes(step.id)}
                      onChange={() => toggleStep(step.id)}
                    />
                    <span>{`${step.key} (${step.phase})`}</span>
                  </label>
                ))
              ) : (
                <span className="text-sm text-gray-500">This scenario has no steps yet.</span>
              )}
            </div>
          </div>
        )}

        {draft.evaluator === 'fact_match' && (
          <LabSpecBuilder
            schema={LAB_JSON_SCHEMAS.criterionFactAssertion}
            value={draft.fact_assertion}
            onChange={setJson('fact_assertion')}
            resetKey={formSeed}
            context={specContext}
          >
            <p className="m-0 text-xs text-gray-500">
              The path has to be a fact some step actually emits. Nothing emits it, nothing ever
              matches, and the criterion stays pending for the whole run rather than failing
              loudly.
            </p>
          </LabSpecBuilder>
        )}

        {draft.evaluator === 'device_health' && (
          <LabSpecBuilder
            schema={LAB_JSON_SCHEMAS.criterionHealthAssertion}
            value={draft.health_assertion}
            onChange={setJson('health_assertion')}
            resetKey={formSeed}
            context={specContext}
          />
        )}
      </div>

      {/* Authored over this form rather than instead of it: a criterion is usually written while
          thinking about the step it scores, and sending an author back to the step list to add one
          would lose everything typed here. The new step is ticked on the way back - it shows in the
          list once the section reloads, which is the same refresh that brings it into the tree. */}
      <LabStepEditorModal
        open={newStep}
        scenario={scenario}
        step={null}
        onClose={() => setNewStep(false)}
        onSaved={(saved) =>
          setDraft((current) => ({
            ...current,
            steps: [...(current.steps || []), saved.id],
          }))
        }
      />
    </StbModal>
  );
};

export default LabCriterionEditorModal;
